import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type ViewStyle } from 'react-native';
import {
  type AnimatedStyle,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withSequence,
  withTiming,
} from 'react-native-reanimated';

import { DURATION } from '@/constants/animation';

/**
 * Perspective is what makes the turn read as a surface catching wind rather
 * than as the badge squashing horizontally.
 *
 * Counter-intuitively a larger number is a weaker effect: it places the viewer
 * further away, so the near edge grows less as it turns. Deliberately far, to
 * keep the movement from reading as the label distorting.
 */
const WAVE_PERSPECTIVE = 700;

/**
 * The pole. Rotating about the center reads as a card flipping, because both
 * edges travel the same distance in opposite directions; cloth has one end held
 * and one end free. Anchoring the origin to the left edge is what turns the
 * same rotation into a flap.
 */
const WAVE_ORIGIN = 'left center';

/**
 * Degrees of the flap, decaying. Enough swings to read as flutter rather than a
 * single turn, and decaying because a flag settles.
 *
 * Halved from the first version. At 26 degrees it read as the badge demanding
 * to be looked at, which is the wrong register for a marker sitting beside a
 * clinician's own name on a card they did not open to be sold to. The point is
 * to be noticed on the way past, not to interrupt.
 *
 * The peak has stayed at 11 through every pass since, deliberately. Asked to
 * make this more noticeable, the obvious move is to swing further, and that is
 * the one change that reads as flashy rather than as visible. Visibility here
 * is bought with time instead: one more swing, and a slower one. See
 * `WAVE_SWING_MS`.
 */
const WAVE_DEGREES = [-11, 6, -3, 1, 0];

/**
 * The trailing edge lags the flap, which is the part that reads as fabric
 * rather than as a rigid panel on a hinge. Scaled down with the flap: the skew
 * has to stay proportional to the turn or the cloth reads as slack.
 */
const LAG_DEGREES = [1.4, -0.7, 0.4, -0.1, 0];

/**
 * One frame behind the flap. The lag has to resolve after the turn that caused
 * it or the two read as a single rigid movement.
 */
const LAG_PHASE_MS = 55;

/**
 * How long one swing takes.
 *
 * `DURATION.normal` rather than `fast`. At 200ms per swing the whole flap was
 * over in 800ms, and a small movement that quick reads as a twitch: the eye
 * registers that something moved without having time to see what. The same
 * distance covered over 300ms reads as deliberate, and the sequence now runs
 * 1.5s, which is long enough to catch someone whose eye was elsewhere when it
 * started.
 *
 * This is the lever to reach for when the marker needs to be more noticeable.
 * Slower and longer costs nothing in loudness. A wider swing does.
 */
const WAVE_SWING_MS = DURATION.normal;

/**
 * How much of the badge has to be on screen before the flap counts as seen.
 *
 * The whole thing. It is a small element, so a lower threshold would fire while
 * it was still a sliver at the edge of the viewport, which spends the animation
 * on a moment the reader cannot watch.
 */
const IN_VIEW_THRESHOLD = 1;

/**
 * A single flag flap that draws the eye to a "new" marker, then settles.
 *
 * Shared rather than reimplemented per surface, so the badges carrying it
 * cannot drift into two different movements. The badge itself is already shared
 * for that reason; the motion pointing at it should be too.
 *
 * Two values rather than one, and this is what separates cloth from a card
 * flip. `turn` swings the badge about its left edge, so the free edge travels
 * and the anchored edge does not. `lag` skews the trailing edge a fraction of a
 * beat later, so the shape deforms as it moves instead of staying rigid. Either
 * alone reads as a panel on a hinge.
 *
 * WHEN IT RUNS. Not simply on mount, because on mount is often when nobody is
 * looking: the badge can be below the fold, or the reader is still filtering
 * and has not reached the result yet. Either way the one flap is spent on an
 * empty room. So it runs when the badge is actually on screen, and again when
 * `replayKey` changes, which is how a caller says the thing under the badge is
 * now a different thing, such as a filter resolving to another treatment.
 *
 * It still settles, and it is still finite. Motion that repeats indefinitely
 * alongside other content falls under WCAG's requirement to offer a way to
 * pause it, which is a control no decoration should need, and a marker that
 * never stops waving stops reading as new and starts reading as something wrong
 * with the page. Replaying on a filter the reader themselves triggered is a
 * different thing from looping: it is a response to their action.
 *
 * The delay lets the surface arrive first, so the movement draws the eye to the
 * badge rather than competing with the thing it sits on.
 *
 * PLATFORM. In-view detection is web only, through IntersectionObserver. Native
 * has no equivalent primitive and the honest options there are FlatList
 * viewability callbacks threaded down through the list, or measuring on scroll,
 * neither of which belongs inside a hook that returns a style. On native this
 * keeps the previous behavior and plays on mount, which windowed lists make a
 * fair approximation, since a row mounts near the point it scrolls in. Replay
 * on `replayKey` works on both.
 *
 * Under reduced motion nothing runs. The flap carries no information the label
 * does not already carry, so dropping it is the honest degradation rather than
 * leaving the badge resting at some angle.
 */
export function useNewBadgePulse(options?: { replayKey?: string | number }): {
  style: AnimatedStyle<ViewStyle>;
  /**
   * Attach to the element wrapping the badge. Ignored on native, where there is
   * no node to observe.
   */
  ref: (node: unknown) => void;
} {
  const replayKey = options?.replayKey;

  const turn = useSharedValue(0);

  const lag = useSharedValue(0);

  const prefersReducedMotion = useReducedMotion();

  const [node, setNode] = useState<Element | null>(null);

  /**
   * A callback ref rather than a `useRef`, because the effect below has to run
   * when the element arrives and a ref object mutating does not re-render.
   */
  const ref = useCallback((next: unknown) => {
    setNode(isWeb && next instanceof Element ? next : null);
  }, []);

  /**
   * Whether this badge has already had its flap for the current `replayKey`.
   *
   * Without it the observer fires again every time the badge scrolls back into
   * view, and a marker that waves on every scroll past is the looping animation
   * the comment above rules out.
   */
  const hasPlayed = useRef(false);

  useEffect(() => {
    hasPlayed.current = false;
  }, [replayKey]);

  const play = useCallback(() => {
    if (prefersReducedMotion || hasPlayed.current) {
      return;
    }

    hasPlayed.current = true;

    turn.value = withDelay(
      DURATION.slow,
      withSequence(
        ...WAVE_DEGREES.map((degrees) => withTiming(degrees, { duration: WAVE_SWING_MS }))
      )
    );

    lag.value = withDelay(
      DURATION.slow + LAG_PHASE_MS,
      withSequence(
        ...LAG_DEGREES.map((degrees) => withTiming(degrees, { duration: WAVE_SWING_MS }))
      )
    );
  }, [prefersReducedMotion, turn, lag]);

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    // Native, or a browser without the observer: keep the old behavior rather
    // than never playing at all.
    if (!isWeb || typeof IntersectionObserver === 'undefined' || !node) {
      play();

      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          play();
        }
      },
      { threshold: IN_VIEW_THRESHOLD }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [node, play, prefersReducedMotion, replayKey]);

  const style = useAnimatedStyle(() => ({
    transformOrigin: WAVE_ORIGIN,
    transform: [
      { perspective: WAVE_PERSPECTIVE },
      { rotateY: `${turn.value}deg` },
      { skewY: `${lag.value}deg` },
    ],
  }));

  return { style, ref };
}
